import React, { Fragment, useEffect, useState } from 'react';
import {
  Typography,
  Button,
  Form,
  message,
  Select,
  Modal,
  Spin,
  Checkbox,
  Row,
  Col,
} from 'antd';
import Axios from 'axios';
import { useSelector } from 'react-redux';
import { filterAccessibleSeasons } from '../../../utils/seasonFilter';
// 系列会社ありグループの系列会社 Select（「グループ会社・系列会社構造」PJ Ph.4b）
import { resolveCompanyRule } from '../../../utils/companyRule';
import {
  COMPANY_ALL_VALUE,
  companyChoices,
  normalizeCompanySelection,
  toYmdDash,
} from '../../../utils/companyDeptSource';
import CompanySelectBlock from './CompanySelectBlock';
const { Option } = Select;
const { Title, Text } = Typography;

// E³-Survey(30010) 経年比較集計Excelダウンロード。複数シーズン(年度)を選択して比較。
// 集計・xlsx生成はサーバ(/api/dbModel100/excel30010compare)側で行い、本画面は
// 企業・複数サーベイ・集計種類の選択とダウンロードのトリガーのみを担う。
const Excel30010CompareDownloadPage = () => {
  const [groups, setGroups] = useState([]);
  const [selGroupInfo, setSelGroupInfo] = useState([]);
  const [selGroupCode, setSelGroupCode] = useState('');
  const [groupSurveySeasonInfo, setGroupSurveySeasonInfo] = useState([]);
  const [selSurveySeasonInfo, setSelSurveySeasonInfo] = useState([]);
  const [selSurveySeasonId, setSelSurveySeasonId] = useState([]);
  const [excelType, setExcelType] = useState(0);
  const [submitSW, setSubmitSW] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [userInfo, setUserInfo] = useState({});
  // 系列会社 Select（系列会社ありグループのみ表示。'' = 全社（グループ全体））
  const [companyNames, setCompanyNames] = useState([]);
  const [selCompany, setSelCompany] = useState(COMPANY_ALL_VALUE);

  const usr = useSelector((state) => state.user);

  const readDB = async () => {
    try {
      if (!usr.userData) return null;
      setUserInfo(usr.userData);

      const groupInfo = await Axios.get('/api/group/30010');

      if (usr.userData.groupCode === 'RECRNT' && usr.userData.role === 2) {
        const nonGroup = groupInfo.data.groups.filter(
          (item) => item.group_code !== 'RECRNT'
        );
        setGroups(nonGroup);
      } else {
        setGroups(groupInfo.data.groups);
      }

      if (usr.userData.role === 3) {
        let sgi = groupInfo.data.groups.find(
          (item) => item.group_code === usr.userData.groupCode
        );
        setSelGroupCode(usr.userData.groupCode);
        setSelGroupInfo(sgi);
        setGroupSurveySeasonInfo(
          filterAccessibleSeasons(sgi.survey_season, usr.userData, ['30010', '90301'])
        );
        loadCompanyChoices(usr.userData.groupCode, sgi, toYmdDash(), COMPANY_ALL_VALUE);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    readDB();
  }, [usr]);

  // ---- 系列会社 Select（「グループ会社・系列会社構造」PJ Ph.4b）----
  // 系列会社ありグループ（Group.has_affiliates === true）か
  const isAffiliate = !!selGroupInfo && selGroupInfo.has_affiliates === true;

  // 系列会社の選択肢を読み込み、選択値を補正して返す。系列会社なしグループは API を呼ばず '' を返す
  // （本画面は role [2,9] のみのため role 7 の会社制限は無い）
  const loadCompanyChoices = async (groupCode, sgi, dateStr, current) => {
    if (!sgi || sgi.has_affiliates !== true) {
      setCompanyNames([]);
      setSelCompany(COMPANY_ALL_VALUE);
      return COMPANY_ALL_VALUE;
    }
    const rule = await resolveCompanyRule(groupCode, sgi, dateStr);
    const choices = companyChoices(rule.names, null);
    const next = normalizeCompanySelection(current, choices);
    setCompanyNames(choices.names);
    setSelCompany(next);
    return next;
  };

  const onChangeCompany = (value) => {
    setSelCompany(value || COMPANY_ALL_VALUE);
  };

  const onChangeGroup = (value) => {
    let sgi = groups.find((item) => item.group_code === value);
    setSelGroupCode(value);
    setSelGroupInfo(sgi);
    // 系列会社は企業変更で「全社」に戻す（系列会社ありグループのみ選択肢を取得）
    setSelCompany(COMPANY_ALL_VALUE);
    setCompanyNames([]);
    loadCompanyChoices(value, sgi, toYmdDash(), COMPANY_ALL_VALUE);
    setGroupSurveySeasonInfo(
      filterAccessibleSeasons(sgi.survey_season, usr.userData, ['30010', '90301'])
    );
    setSelSurveySeasonId([]);
    setSelSurveySeasonInfo([]);
  };

  const onChangeSurveySeason = async (value) => {
    // Checkbox.Group → value は選択された _id の配列
    let sssiArr = [];
    for (let i = 0; i < value.length; i++) {
      sssiArr.push(groupSurveySeasonInfo.find((item) => item._id === value[i]));
    }
    setSelSurveySeasonId(value);
    setSelSurveySeasonInfo(sssiArr);
  };

  const onChangeExcelType = (v) => {
    setExcelType(v);
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    try {
      message.loading({
        content: 'データ作成中...',
        key: 'makecsv',
        duration: 20,
      });
      setIsModalVisible(true);
      setSubmitSW(false);

      const res = await Axios.post(
        '/api/dbModel100/excel30010compare',
        {
          group_code: selGroupCode,
          survey_season_ids: selSurveySeasonId,
          excelType: excelType,
          // 系列会社ありグループのみ company（'' = 全社）を送る（系列会社なしグループは従来どおりキー無し）
          ...(isAffiliate ? { company: selCompany } : {}),
        },
        { responseType: 'blob' }
      );

      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // 系列会社で絞り込んだ場合はファイル名に系列会社名を付ける（系列会社なしグループ・全社は従来どおり）
      const companySuffix = isAffiliate && selCompany ? `_${selCompany}` : '';
      a.download = `Recurrent_E3-Sruvey_Summery_Type_${excelType}_${'経年集計'}${companySuffix}.xlsx`;
      a.click();
      a.remove();
      URL.revokeObjectURL(url);

      message.success({
        content: 'ダウンロード完了！',
        key: 'makecsv',
        duration: 2,
      });
      setSubmitSW(true);
      setIsModalVisible(false);
    } catch (error) {
      console.log(error.message);
      setIsModalVisible(false);
      message.error({
        content: error.message,
        key: 'makecsv',
        duration: 4,
      });
      setSubmitSW(true);
    }
  };

  return (
    <Fragment>
      <div style={{ maxWidth: '1100px', margin: '2rem auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <Title level={2}>E³-Survey経年比較集計データダウンロード(Excel)</Title>
        </div>
        <Form onSubmit={onSubmit}>
          <br />
          <Title style={{ color: 'var(--brand-primary)' }}>❶</Title>
          <Text strong style={{ display: 'block', marginBottom: 'var(--space-4)' }}>
            企業を選択してください。（必須）
          </Text>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Select
              showSearch
              style={{ marginLeft: 0, marginRight: 10, width: 300 }}
              placeholder="企業選択"
              optionFilterProp="children"
              onChange={onChangeGroup}
              value={selGroupCode}
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              disabled={userInfo.role === 3}
            >
              {groups.map((element) => {
                return (
                  <Option value={element.group_code} key={element._id}>
                    {element.group_name}
                  </Option>
                );
              })}
            </Select>
          </div>
          <br />
          <div>
            <Title style={{ color: 'var(--brand-primary)' }}>❷</Title>
            <Text strong style={{ display: 'block', marginBottom: 'var(--space-4)' }}>
              サーベイを選択してください。（複数選択可・必須）
            </Text>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Checkbox.Group onChange={onChangeSurveySeason} value={selSurveySeasonId}>
                <Row>
                  {groupSurveySeasonInfo.map((element) => {
                    return (
                      <Col span={24} key={element._id}>
                        <Checkbox value={element._id}>
                          {element.season_name}
                        </Checkbox>
                      </Col>
                    );
                  })}
                </Row>
              </Checkbox.Group>
            </div>
          </div>
          {/* 複数シーズンを比較するため、系列会社の選択肢は今日時点の一覧（シーズンでは切り替えない） */}
          {isAffiliate && (
            <CompanySelectBlock
              names={companyNames}
              value={selCompany}
              onChange={onChangeCompany}
            />
          )}
          <Title style={{ color: 'var(--brand-primary)' }}>❸</Title>
          <Select
            showSearch
            style={{ width: 200 }}
            placeholder="種類選択"
            optionFilterProp="children"
            onChange={onChangeExcelType}
            value={excelType}
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            <Option value={0} key={0}>
              組織別集計
            </Option>
            <Option value={1} key={1}>
              等級別集計
            </Option>
            <Option value={2} key={2}>
              年代別集計
            </Option>
            <Option value={3} key={3}>
              役職別集計
            </Option>
          </Select>
          <Title style={{ color: 'var(--brand-primary)' }}>❹</Title>
          <Button
            type="default"
            className="ds-btn-secondary"
            size="large"
            onClick={onSubmit}
            disabled={!(submitSW && selSurveySeasonId.length)}
          >
            集計データダウンロード（Excel）
          </Button>
        </Form>
      </div>
      <Modal
        title="結果作成中..."
        open={isModalVisible}
        okButtonProps={{ disabled: true }}
        cancelButtonProps={{ disabled: true }}
        styles={{ body: { textAlign: 'center' } }}
      >
        <Spin size="large" />
      </Modal>
    </Fragment>
  );
};

export default Excel30010CompareDownloadPage;
